import { useState, useRef, useCallback } from 'react'

interface UseVideoRecorderResult {
  isRecording: boolean
  isPending: boolean
  videoBlob: Blob | null
  previewUrl: string | null
  startRecording: () => void
  stopRecording: () => void
  cancelRecording: () => void
  error: string | null
  streamRef: React.MutableRefObject<MediaStream | null>
}

export function useVideoRecorder(): UseVideoRecorderResult {
  const [isRecording, setIsRecording] = useState(false)
  const [isPending, setIsPending] = useState(false)
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const mediaRecorder = useRef<MediaRecorder | null>(null)
  const videoChunks = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  const shouldStopRef = useRef(false)
  const shouldCancelRef = useRef(false)

  const startRecording = useCallback(() => {
    setError(null)
    setVideoBlob(null)
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)
    }
    videoChunks.current = []
    shouldStopRef.current = false
    shouldCancelRef.current = false
    setIsPending(true)

    navigator.mediaDevices
      .getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 480 },
          height: { ideal: 480 },
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        },
      })
      .then((stream) => {
        if (shouldCancelRef.current) {
          stream.getTracks().forEach((track) => track.stop())
          setIsPending(false)
          return
        }

        streamRef.current = stream

        // Prefer webm for better compression
        const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
          ? 'video/webm;codecs=vp9,opus'
          : MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
          ? 'video/webm;codecs=vp8,opus'
          : 'video/webm'

        const recorder = new MediaRecorder(stream, { mimeType })
        mediaRecorder.current = recorder

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            videoChunks.current.push(event.data)
          }
        }

        recorder.onstop = () => {
          if (!shouldCancelRef.current && videoChunks.current.length > 0) {
            const blob = new Blob(videoChunks.current, { type: mimeType })
            setVideoBlob(blob)
            const url = URL.createObjectURL(blob)
            setPreviewUrl(url)
          }

          // Clean up stream
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop())
            streamRef.current = null
          }
        }

        recorder.start(100)
        setIsPending(false)
        setIsRecording(true)

        if (shouldStopRef.current) {
          recorder.stop()
          setIsRecording(false)
        }
      })
      .catch((err) => {
        const message = err instanceof Error ? err.message : 'Failed to access camera'
        setError(message)
        setIsPending(false)
        console.error('Video recording error:', err)
      })
  }, [previewUrl])

  const stopRecording = useCallback(() => {
    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop()
      setIsRecording(false)
    } else {
      shouldStopRef.current = true
    }
  }, [])

  const cancelRecording = useCallback(() => {
    shouldCancelRef.current = true

    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop()
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }

    videoChunks.current = []
    setVideoBlob(null)
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)
    }
    setIsRecording(false)
    setIsPending(false)
  }, [previewUrl])

  return {
    isRecording,
    isPending,
    videoBlob,
    previewUrl,
    startRecording,
    stopRecording,
    cancelRecording,
    error,
    streamRef,
  }
}

// Extract frame from video blob for analysis
export async function extractFrameFromVideo(videoBlob: Blob): Promise<string | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video')
    video.muted = true
    video.playsInline = true

    const url = URL.createObjectURL(videoBlob)
    video.src = url

    video.onloadeddata = () => {
      // Seek to middle of video
      video.currentTime = video.duration / 2
    }

    video.onseeked = () => {
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext('2d')

      if (ctx) {
        ctx.drawImage(video, 0, 0)
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8)
        const base64 = dataUrl.split(',')[1]
        URL.revokeObjectURL(url)
        resolve(base64)
      } else {
        URL.revokeObjectURL(url)
        resolve(null)
      }
    }

    video.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(null)
    }
  })
}
